/**
 * Formatting Utilities
 * Number and size formatting functions
 */

export type TranslationFunction = (key: string) => string;

/** Default unit labels for formatFileSize when no translation function is provided */
const defaultUnitLabels: Record<string, string> = {
  'units.bytes': 'B',
  'units.kb': 'KB',
  'units.mb': 'MB',
  'units.gb': 'GB',
};

/**
 * Format file size in human-readable format
 * @param bytes - File size in bytes
 * @param t - Translation function for units
 * @returns Formatted file size
 */
export function formatFileSize(
  bytes: number | null | undefined,
  t: TranslationFunction = (key: string) => defaultUnitLabels[key] || key
): string {
  if (bytes === null || bytes === undefined) return '';
  if (bytes === 0) return `0 ${t('units.bytes')}`;

  const k = 1024;
  if (bytes < k) return `${bytes} ${t('units.bytes')}`;
  if (bytes < k * k) return `${(bytes / k).toFixed(1)} ${t('units.kb')}`;
  if (bytes < k * k * k)
    return `${(bytes / (k * k)).toFixed(1)} ${t('units.mb')}`;
  return `${(bytes / (k * k * k)).toFixed(1)} ${t('units.gb')}`;
}

/** Math symbols for ODRL comparator IRIs (matched on the last path segment). */
const COMPARATOR_SYMBOLS: Record<string, string> = {
  eq: '=',
  neq: '≠',
  gt: '>',
  gteq: '≥',
  lt: '<',
  lteq: '≤',
};

/** Last path or fragment segment of an IRI; returns the input unchanged if it has none. */
function iriLastSegment(iri: string): string {
  const segment = iri.split(/[/#]/).pop();
  return segment ? segment : iri;
}

/**
 * Render a normative requirement (comparator, value, unit) as a compact,
 * human-readable string, e.g. `≥ 1.2 m`. Comparators given as ODRL IRIs
 * (`http://www.w3.org/ns/odrl/2/gteq`) become math symbols and units given as
 * QUDT IRIs (`http://qudt.org/vocab/unit/m`) collapse to their last segment.
 * Already-clean values pass through unchanged, so this is safe to call twice.
 */
export function formatRequirement(
  comparator: string | null | undefined,
  value: string | null | undefined,
  unit: string | null | undefined
): string {
  const parts: string[] = [];
  if (comparator) {
    const segment = iriLastSegment(comparator);
    parts.push(COMPARATOR_SYMBOLS[segment] ?? segment);
  }
  if (value) parts.push(String(value));
  if (unit) parts.push(iriLastSegment(unit));
  return parts.join(' ');
}
